import React, { useState, useRef, useEffect } from 'react';
import {
  GALLERY_CATEGORIES,
  MORE_CATEGORY_OPTIONS,
  ALL_GALLERY_FILTER_OPTIONS,
} from './galleryFilters';

const MOBILE_MQ = '(max-width: 768px)';

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(MOBILE_MQ).matches : false
  );

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_MQ);
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  return isMobile;
}

const ICONS = {
  all: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <rect x="1" y="1" width="6" height="6" rx="1" />
      <rect x="9" y="1" width="6" height="6" rx="1" />
      <rect x="1" y="9" width="6" height="6" rx="1" />
      <rect x="9" y="9" width="6" height="6" rx="1" />
    </svg>
  ),
  birthdays: <span aria-hidden="true">🎂</span>,
  christmas: <span aria-hidden="true">🎄</span>,
  crafts: <span aria-hidden="true">✂</span>,
  celebrations: <span aria-hidden="true">🎈</span>,
  more: <span aria-hidden="true">⋯</span>,
};

function GalleryToolbar({
  categoryId,
  onCategoryChange,
  searchQuery,
  onSearchChange,
  countBeforeSearch,
}) {
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef(null);
  const isMobile = useIsMobile();
  const isMoreActive = MORE_CATEGORY_OPTIONS.some((o) => o.id === categoryId);

  useEffect(() => {
    const close = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false);
    };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, []);

  const searchPlaceholder =
    searchQuery.trim().length > 0
      ? 'Keresés…'
      : `Összesen ${countBeforeSearch} kép`;

  return (
    <section className="gallery-toolbar" aria-label="Szűrők és keresés">
      {isMobile ? (
        <div className="gallery-toolbar-filter-select-wrap">
          <label className="gallery-toolbar-filter-select-label" htmlFor="gallery-category-select">
            Kategória
          </label>
          <select
            id="gallery-category-select"
            className="gallery-toolbar-filter-select"
            value={categoryId}
            onChange={(e) => onCategoryChange(e.target.value)}
            aria-label="Galéria kategória szűrő"
          >
            {ALL_GALLERY_FILTER_OPTIONS.map((opt) => (
              <option key={opt.id} value={opt.id}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      ) : (
        <div className="gallery-toolbar-pills">
          {GALLERY_CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              type="button"
              className={`gallery-pill${categoryId === cat.id ? ' gallery-pill--active' : ''}`}
              onClick={() => onCategoryChange(cat.id)}
            >
              <span className="gallery-pill-icon">{ICONS[cat.id]}</span>
              {cat.label}
            </button>
          ))}

          <div className="gallery-pill-more" ref={moreRef}>
            <button
              type="button"
              className={`gallery-pill${isMoreActive ? ' gallery-pill--active' : ''}`}
              onClick={() => setMoreOpen((o) => !o)}
              aria-expanded={moreOpen}
              aria-haspopup="true"
            >
              <span className="gallery-pill-icon">{ICONS.more}</span>
              Továbbiak
              <span className="gallery-pill-caret" aria-hidden="true">
                ▾
              </span>
            </button>
            {moreOpen && (
              <ul className="gallery-pill-dropdown" role="menu">
                {MORE_CATEGORY_OPTIONS.map((opt) => (
                  <li key={opt.id} role="none">
                    <button
                      type="button"
                      role="menuitem"
                      className={categoryId === opt.id ? 'is-active' : ''}
                      onClick={() => {
                        onCategoryChange(opt.id);
                        setMoreOpen(false);
                      }}
                    >
                      {opt.label}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      <div className="gallery-toolbar-search">
        <span className="gallery-toolbar-search-icon" aria-hidden="true">
          ⌕
        </span>
        <input
          type="search"
          className="gallery-toolbar-search-input"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={searchPlaceholder}
          aria-label="Keresés a bejegyzések szövegében"
          autoComplete="off"
        />
        {searchQuery.trim() && (
          <button
            type="button"
            className="gallery-toolbar-search-clear"
            onClick={() => onSearchChange('')}
            aria-label="Keresés törlése"
          >
            ✕
          </button>
        )}
      </div>
    </section>
  );
}

export default GalleryToolbar;
